import { Icon } from '@iconify/react';
import {
  Accordion,
  AccordionItem,
  BreadcrumbItem,
  Breadcrumbs,
  Card,
  Divider,
  ScrollShadow,
  Spinner,
  Tooltip,
} from '@nextui-org/react';
import {
  ReactFlow,
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { useGetPromptById } from '@/hooks/queries/prompts';
import { useNavigation } from '@/hooks/useNavigation';
import Agent from './flow-components/agent';
import ChatInputNode from './flow-components/chat-input-node';
import ChatOutputNode from './flow-components/chat-output-node';
import OpenAINode from './flow-components/open-ai-node';
import PromptNode from './flow-components/prompt-node';

const rfStyle = {
  backgroundColor: '#f7f6f2',
};

const defaultAgentNode = {
  id: 'agent',
  type: 'difficultConversationAgent',
  position: { x: 450, y: 125 },
  data: {
    title: 'AI Agent',
    agentId: 'default',
    description: 'Define the agent instructions, then enter a task to complete',
    systemPrompt: '',
    modelOptions: [
      { key: 'gpt-4', value: 'GPT-4' },
      { key: 'gpt-3.5-turbo', value: 'GPT-3.5 Turbo' },
    ],
    inputs: [
      {
        id: 'input',
        label: 'Input Message',
        isReadOnly: false,
        value: '',
      },
      {
        id: 'system-prompt',
        label: 'System Prompt',
        isReadOnly: false,
        value: '',
      },
    ],
    outputs: [
      {
        id: 'response',
      },
    ],
    style: {
      iconColor: 'blue',
      handleColor: 'blue',
    },
    showTemperature: true,
    temperature: 0.7,
  },
};

const defaultNodes = [
  {
    id: 'chat-input',
    type: 'chatInput',
    position: { x: 100, y: 50 },
    data: { label: 'Chat Input' },
  },
  defaultAgentNode,
  {
    id: 'chat-output',
    type: 'chatOutput',
    position: { x: 900, y: 125 },
    data: { label: 'Chat Output' },
  },
];

const defaultEdges = [
  {
    id: 'edge-input-to-agent',
    source: 'chat-input',
    target: 'agent',
    sourceHandle: 'message-input-1',
    targetHandle: 'input',
  },
  {
    id: 'edge-agent-to-output',
    source: 'agent',
    target: 'chat-output',
    sourceHandle: 'response',
    targetHandle: 'input',
  },
];

const nodeTypes = {
  chatInput: (x) => {
    return (
      <ChatInputNode
        data={{
          label: 'Input',
          description: 'Enter your message here',
          placeholder: 'Type something...',
          defaultValue: 'Hello',
          onInputChange: (value) => console.log(value),
          handleId: 'message-input-1',
        }}
      />
    );
  },
  prompt: PromptNode,
  agent: OpenAINode,
  chatOutput: ChatOutputNode,
  difficultConversationAgent: Agent,
};

const ComponentCard = ({ title, icon, nodeType, onDragStart, onAdd }) => {
  return (
    <Card
      isPressable
      className="py-2 px-3 w-full bg-default-100 group"
      draggable
      onDragStart={(e) => onDragStart(e, nodeType)}
      shadow="none"
    >
      <div className="flex items-center justify-between w-full">
        <span className="text-sm">{title}</span>
        <div className="flex items-center space-x-2">
          <div className="flex cursor-move p-2 hover:bg-gray-100 rounded-lg gap-1">
            <div
              className="cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity rounded-lg"
              onClick={() => onAdd(nodeType)}
            >
              <Icon icon="mdi:plus" className="w-5 h-5 text-gray-600" />
            </div>
            <Icon icon={icon} className="w-5 h-5 text-gray-600" />
          </div>
        </div>
      </div>
    </Card>
  );
};

const Sidebar = ({ onAdd }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const onDragStart = (event, nodeType) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
  };

  return (
    <div
      className={`transition-all duration-300 ease-in-out flex ${isCollapsed ? 'w-12' : 'w-64'}`}
    >
      <ScrollShadow
        className={`bg-white h-full ${isCollapsed ? 'w-12' : 'w-64'} relative`}
      >
        <div className="flex items-center p-4">
          <h2
            className={`text-medium font-semibold ${isCollapsed ? 'hidden' : 'block'}`}
          >
            Components
          </h2>
          <Tooltip
            hidden={!isCollapsed}
            content={isCollapsed ? 'Components' : ''}
          >
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="p-2 hover:bg-gray-100 rounded-lg absolute right-2"
            >
              <Icon
                icon={isCollapsed ? 'mdi:chevron-right' : 'mdi:chevron-left'}
                className="w-5 h-5 text-gray-600"
              />
            </button>
          </Tooltip>
        </div>

        <div className={isCollapsed ? 'hidden' : 'block px-3'}>
          <Accordion
            className="text-small"
            selectionMode="multiple"
            itemClasses={{ title: 'text-small' }}
          >
            <AccordionItem key="inputs" aria-label="Inputs" title="Inputs">
              <div className="space-y-2">
                <ComponentCard
                  title="Chat Input"
                  icon="mdi:drag"
                  nodeType="chatInput"
                  onDragStart={onDragStart}
                  onAdd={onAdd}
                />
                <ComponentCard
                  title="Prompt"
                  icon="mdi:drag"
                  nodeType="prompt"
                  onDragStart={onDragStart}
                  onAdd={onAdd}
                />
              </div>
            </AccordionItem>

            <AccordionItem key="outputs" title="Outputs">
              <div className="space-y-2">
                <ComponentCard
                  title="Chat Output"
                  icon="mdi:drag"
                  nodeType="chatOutput"
                  onDragStart={onDragStart}
                  onAdd={onAdd}
                />
              </div>
            </AccordionItem>

            <AccordionItem key="agents" title="Agents">
              <div className="space-y-2">
                <ComponentCard
                  title="Agent"
                  icon="mdi:drag"
                  nodeType="agent"
                  onDragStart={onDragStart}
                  onAdd={onAdd}
                />
                {/* <ComponentCard
                  title="Difficult Conversation Agent"
                  icon="mdi:drag"
                  nodeType="difficultConversationAgent"
                  onDragStart={onDragStart}
                  onAdd={onAdd}
                /> */}
              </div>
            </AccordionItem>
          </Accordion>
        </div>
      </ScrollShadow>
    </div>
  );
};

function Flow() {
  const { navigateToTenantRoute } = useNavigation();
  const location = useLocation();
  const { id: agentId } = useParams<{
    id: string;
    tenantSlug: string;
  }>();
  const agentNameFromState = location.state?.agentName as string | undefined;

  const [nodes, setNodes] = useState(defaultNodes);
  const [edges, setEdges] = useState(defaultEdges);
  const { data: agentData, isLoading: isAgentLoading } =
    useGetPromptById(agentId);
  const agentName = agentNameFromState || agentData?.name || 'Agent';

  // Update nodes when agent data is available
  useEffect(() => {
    if (agentData?.id) {
      setNodes((currentNodes) => {
        return currentNodes.map((node) => {
          if (node.id === 'agent') {
            console.log('coming.....', agentData);
            return {
              ...node,
              data: {
                ...node.data,
                title: agentData.name || node.data.title,
                agentId: agentData.id,
                description: agentData?.description || node.data?.description,
                systemPrompt: agentData.system_prompt,
                inputs: [
                  {
                    id: 'input',
                    label: 'Input Message',
                    isReadOnly: false,
                  },
                  {
                    id: 'system-prompt',
                    label: 'System Prompt',
                    isReadOnly: false,
                    defaultValue: agentData.system_prompt,
                  },
                ],
                temperature: agentData.temperature || node.data.temperature,
              },
            };
          }
          return node;
        });
      });
    }
  }, [agentData]);

  // Keep existing callbacks
  const onNodesChange = useCallback(
    (changes) => setNodes((nds) => applyNodeChanges(changes, nds)),
    [setNodes]
  );
  const onEdgesChange = useCallback(
    (changes) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    [setEdges]
  );
  const onConnect = useCallback(
    (connection) => setEdges((eds) => addEdge(connection, eds)),
    [setEdges]
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const type = event.dataTransfer.getData('application/reactflow');
      if (!type) return;

      const position = {
        x: event.clientX - event.target.getBoundingClientRect().left,
        y: event.clientY - event.target.getBoundingClientRect().top,
      };

      const newNode = {
        id: `${type}-${nodes.length + 1}`,
        type,
        position,
        data: { label: `${type} node` },
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [nodes]
  );

  const onAdd = (nodeType) => {
    const newNode = {
      id: `${nodeType}-${nodes.length + 1}`,
      type: nodeType,
      position: { x: 250, y: 250 },
      data: { label: `${nodeType} node` },
    };
    setNodes((nds) => nds.concat(newNode));
  };

  if (isAgentLoading) {
    return (
      <div className="h-screen w-full flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  // Keep your existing return statement with ReactFlow
  return (
    <div className="flex flex-col h-screen">
      <Breadcrumbs className="p-4">
        <BreadcrumbItem onClick={() => navigateToTenantRoute('/agents')}>
          Agents
        </BreadcrumbItem>
        <BreadcrumbItem
          onClick={() => navigateToTenantRoute(`/agents/${agentId}/edit`)}
        >
          {agentName}
        </BreadcrumbItem>
        <BreadcrumbItem>Flow</BreadcrumbItem>
      </Breadcrumbs>
      <Divider />
      <div className="flex flex-1">
        <Sidebar onAdd={onAdd} />
        <div className="flex-1">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onDragOver={onDragOver}
            onDrop={onDrop}
            nodeTypes={nodeTypes}
            fitView
            style={rfStyle}
          />
        </div>
      </div>
    </div>
  );
}

export default Flow;
